import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { columns, actionOptions, statusOptions, formatDateTime } from "./formColumn";

import {
  Button,
  ConfigProvider,
  DatePicker,
  Descriptions,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Table,
  message,
} from "antd";

const API_BASE = import.meta.env.VITE_BASE_URL2;
// Endpoint audit trail (model: mobile.audit_trail)
const AUDIT_TRAIL_URL = `${API_BASE}/api/audit-trail`;

const { RangePicker } = DatePicker;

const JsonBlock = ({ value }) => {
  if (value === null || typeof value === "undefined") return <span>-</span>;
  return (
    <pre
      style={{
        margin: 0,
        maxHeight: 260,
        overflow: "auto",
        background: "#f5f5f5",
        padding: 12,
        borderRadius: 6,
        fontSize: 12,
      }}
    >
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
};

// Kolom yang ikut dicari lewat search box
const SEARCH_FIELDS = [
  "username",
  "role_name",
  "module",
  "menu_name",
  "action",
  "record_id",
  "description",
  "endpoint",
  "ip_address",
  "status",
  "source",
];

const AuditTrail = () => {
  // Controller getAuditTrail mengembalikan seluruh row tanpa pagination,
  // jadi filter & pagination dikerjakan di sisi client.
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await axios.get(AUDIT_TRAIL_URL);
      const rows = resp.data?.data_audit_trail ?? [];

      // urutkan terbaru di atas
      const sorted = [...rows].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setAllLogs(sorted);
      setPage(1);
    } catch (err) {
      console.error("fetchAuditTrail error", err);
      message.error("Gagal mengambil data audit trail");
      setAllLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    const start = dateRange?.[0] ? dateRange[0].startOf("day").valueOf() : null;
    const end = dateRange?.[1] ? dateRange[1].endOf("day").valueOf() : null;

    return allLogs.filter((log) => {
      if (
        actionFilter &&
        String(log.action || "").toUpperCase() !== actionFilter
      ) {
        return false;
      }

      if (
        statusFilter &&
        String(log.status || "").toUpperCase() !== statusFilter
      ) {
        return false;
      }

      if (start || end) {
        const ts = new Date(log.created_at).getTime();
        if (Number.isNaN(ts)) return false;
        if (start && ts < start) return false;
        if (end && ts > end) return false;
      }

      if (keyword) {
        const match = SEARCH_FIELDS.some((field) =>
          String(log[field] ?? "")
            .toLowerCase()
            .includes(keyword)
        );
        if (!match) return false;
      }

      return true;
    });
  }, [allLogs, q, actionFilter, statusFilter, dateRange]);

  const pagedLogs = useMemo(
    () => filteredLogs.slice((page - 1) * limit, page * limit),
    [filteredLogs, page, limit]
  );

  // Field opsional hanya ditampilkan kalau logs-nya memang punya isi,
  // supaya modal tidak penuh baris "-".
  const detailItems = useMemo(() => {
    if (!selectedLog) return [];

    const log = selectedLog;
    const items = [
      { key: "created_at", label: "Waktu", children: formatDateTime(log.created_at) },
      {
        key: "user",
        label: "User",
        children: `${log.username || "-"} (${log.user_id ?? "-"})`,
      },
      { key: "role_name", label: "Role", children: log.role_name || "-" },
      { key: "module", label: "Module", children: log.module || "-" },
      { key: "menu_name", label: "Menu", children: log.menu_name || "-" },
      { key: "action", label: "Action", children: log.action || "-" },
      { key: "record_id", label: "Record ID", children: log.record_id || "-" },
      {
        key: "status",
        label: "Status",
        children: `${log.status || "-"}${
          log.response_code ? ` (${log.response_code})` : ""
        }`,
      },
      {
        key: "endpoint",
        label: "Endpoint",
        span: 2,
        children: `${log.http_method || ""} ${log.endpoint || "-"}`.trim(),
      },
      {
        key: "description",
        label: "Deskripsi",
        span: 2,
        children: log.description || "-",
      },
      { key: "ip_address", label: "IP Address", children: log.ip_address || "-" },
      { key: "source", label: "Source", children: log.source || "-" },
      { key: "browser", label: "Browser", children: log.browser || "-" },
      { key: "operating_system", label: "OS", children: log.operating_system || "-" },
    ];

    const optional = [
      { key: "session_id", label: "Session ID", value: log.session_id },
      { key: "correlation_id", label: "Correlation ID", value: log.correlation_id },
      { key: "request_id", label: "Request ID", value: log.request_id },
      { key: "remarks", label: "Remarks", value: log.remarks, span: 2 },
    ];

    optional.forEach(({ key, label, value, span }) => {
      if (value) items.push({ key, label, span, children: value });
    });

    if (log.before_data) {
      items.push({
        key: "before_data",
        label: "Before Data",
        span: 2,
        children: <JsonBlock value={log.before_data} />,
      });
    }

    if (log.after_data) {
      items.push({
        key: "after_data",
        label: "After Data",
        span: 2,
        children: <JsonBlock value={log.after_data} />,
      });
    }

    return items;
  }, [selectedLog]);

  const handleReset = () => {
    setQ("");
    setActionFilter(null);
    setStatusFilter(null);
    setDateRange(null);
    setPage(1);
  };

  const extendedColumn = [
    ...columns,
    {
      title: "Detail",
      key: "detail",
      fixed: "right",
      width: 90,
      render: (_, record) => (
        <Button type="link" onClick={() => setSelectedLog(record)}>
          Lihat
        </Button>
      ),
    },
  ];

  return (
    <div>
      <h1 className="text-4xl font-semibold mb-8">Audit Trail Logs</h1>

      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(val) => {
              setDateRange(val);
              setPage(1);
            }}
            format="DD-MM-YYYY"
          />
          <Select
            allowClear
            placeholder="Filter Action"
            style={{ width: 160 }}
            options={actionOptions}
            value={actionFilter}
            onChange={(val) => {
              setActionFilter(val ?? null);
              setPage(1);
            }}
          />
          <Select
            allowClear
            placeholder="Filter Status"
            style={{ width: 150 }}
            options={statusOptions}
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val ?? null);
              setPage(1);
            }}
          />
          <Button onClick={handleReset}>Reset</Button>
          <Button onClick={fetchLogs} loading={loading}>
            Refresh
          </Button>
        </Space>

        <Input.Search
          placeholder="Cari username / module / menu / deskripsi"
          allowClear
          enterButton
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          onSearch={(val) => {
            setQ(val);
            setPage(1);
          }}
          style={{ width: 340 }}
        />
      </div>

      <ConfigProvider
        theme={{
          components: {
            Table: {
              colorPrimary: "#0164A2",
              headerColor: "#fff",
              headerBg: "#0164A2",
              headerBorderRadius: 6,
              algorithm: true,
            },
            Pagination: {
              colorPrimary: "#0164A2",
              colorPrimaryHover: "#0164A2",
              colorPrimaryBorder: "#0164A2",
              algorithm: true,
            },
          },
        }}
      >
        <div style={{ position: "relative" }}>
          {loading && (
            <div style={{ position: "absolute", zIndex: 20, right: 16, top: 16 }}>
              <Spin />
            </div>
          )}

          <Table
            columns={extendedColumn}
            dataSource={pagedLogs}
            size="middle"
            rowKey={(record) => record.id}
            pagination={false}
            loading={loading}
            scroll={{ x: "max-content" }}
            className={pagedLogs.length === 0 ? `shadow-md` : ""}
          />
        </div>

        <div className="flex justify-end mt-4">
          <Pagination
            current={page}
            pageSize={limit}
            total={filteredLogs.length}
            onChange={(nextPage, nextSize) => {
              setPage(nextPage);
              setLimit(nextSize);
            }}
            showSizeChanger
            onShowSizeChange={(current, size) => {
              setLimit(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            showTotal={(total) => `Total ${total} items`}
          />
        </div>
      </ConfigProvider>

      <Modal
        open={!!selectedLog}
        title="Detail Audit Trail"
        onCancel={() => setSelectedLog(null)}
        footer={null}
        width={900}
      >
        {selectedLog && (
          <Descriptions bordered size="small" column={2} items={detailItems} />
        )}
      </Modal>
    </div>
  );
};

export default AuditTrail;
